import type { UsageCostFilters, UsageRangePreset } from './types';

const USAGE_COST_REQUEST_TIMEOUT_MS = 35000;

export const USAGE_COST_ENDPOINTS = {
  summary: '/api/admin/usage-costs/summary',
  timeline: '/api/admin/usage-costs/timeline',
  trends: '/api/admin/usage-costs/trends',
  breakdown: '/api/admin/usage-costs/breakdown',
  clinics: '/api/admin/usage-costs/clinics',
  users: '/api/admin/usage-costs/users',
  dataQuality: '/api/admin/usage-costs/data-quality',
  metadata: '/api/admin/usage-costs/metadata',
} as const;

export class UsageCostApiError extends Error {
  status: number;
  details: unknown;

  constructor(message: string, status: number, details: unknown) {
    super(message);
    this.name = 'UsageCostApiError';
    this.status = status;
    this.details = details;
  }
}

const toDateInputValue = (date: Date) => date.toISOString().slice(0, 10);

const startOfMonth = (date: Date) => new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), 1));

const endOfMonth = (date: Date) => new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + 1, 0));

export const getUsageCostRange = (
  preset: UsageRangePreset,
  now = new Date()
): Pick<UsageCostFilters, 'startDate' | 'endDate'> => {
  const today = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));

  if (preset === 'current-month') {
    return {
      startDate: toDateInputValue(startOfMonth(today)),
      endDate: toDateInputValue(today),
    };
  }

  if (preset === 'previous-month') {
    const previousMonth = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth() - 1, 1));
    return {
      startDate: toDateInputValue(startOfMonth(previousMonth)),
      endDate: toDateInputValue(endOfMonth(previousMonth)),
    };
  }

  const days = preset === '7d' ? 7 : 30;
  const startDate = new Date(today);
  startDate.setUTCDate(today.getUTCDate() - days + 1);

  return {
    startDate: toDateInputValue(startDate),
    endDate: toDateInputValue(today),
  };
};

export const buildUsageCostParams = (
  filters: UsageCostFilters,
  extraQuery: Record<string, string | number | boolean | null | undefined> = {}
) => {
  const searchParams = new URLSearchParams();
  searchParams.set('start_date', filters.startDate);
  searchParams.set('end_date', filters.endDate);

  if (filters.clinicId != null) searchParams.set('clinic_id', String(filters.clinicId));
  if (filters.userId != null) searchParams.set('user_id', String(filters.userId));
  if (filters.feature) searchParams.set('feature', filters.feature);
  if (filters.provider) searchParams.set('provider', filters.provider);
  if (filters.model) searchParams.set('model', filters.model);

  for (const [key, value] of Object.entries(extraQuery)) {
    if (value != null && value !== '') {
      searchParams.set(key, String(value));
    }
  }

  return searchParams;
};

export const buildUsageCostMetadataParams = (filters: Pick<UsageCostFilters, 'startDate' | 'endDate'>) => {
  const searchParams = new URLSearchParams();
  searchParams.set('start_date', filters.startDate);
  searchParams.set('end_date', filters.endDate);
  return searchParams;
};

const readErrorPayload = async (response: Response) => {
  try {
    return await response.json();
  } catch {
    return {};
  }
};

export const fetchUsageCostData = async <T>(
  path: string,
  filters: UsageCostFilters,
  extraQuery: Record<string, string | number | boolean | null | undefined> = {},
  options: { signal?: AbortSignal } = {}
): Promise<T> => {
  const controller = options.signal ? null : new AbortController();
  const signal = options.signal ?? controller?.signal;
  const timeout = controller ? window.setTimeout(() => controller.abort(), USAGE_COST_REQUEST_TIMEOUT_MS) : undefined;
  const searchParams = buildUsageCostParams(filters, extraQuery);

  try {
    const response = await fetch(`${path}?${searchParams.toString()}`, {
      credentials: 'include',
      signal,
    });

    if (!response.ok) {
      const payload = await readErrorPayload(response);
      throw new UsageCostApiError(
        payload.error || `Request failed: ${response.status}`,
        response.status,
        payload.details
      );
    }

    const payload = await response.json();
    return payload.data as T;
  } finally {
    if (timeout) window.clearTimeout(timeout);
  }
};

export const fetchUsageCostMetadata = async <T>(
  filters: Pick<UsageCostFilters, 'startDate' | 'endDate'>,
  options: { signal?: AbortSignal } = {}
): Promise<T> => {
  const controller = options.signal ? null : new AbortController();
  const signal = options.signal ?? controller?.signal;
  const timeout = controller ? window.setTimeout(() => controller.abort(), USAGE_COST_REQUEST_TIMEOUT_MS) : undefined;
  const searchParams = buildUsageCostMetadataParams(filters);

  try {
    const response = await fetch(`${USAGE_COST_ENDPOINTS.metadata}?${searchParams.toString()}`, {
      credentials: 'include',
      signal,
    });

    if (!response.ok) {
      const payload = await readErrorPayload(response);
      throw new UsageCostApiError(
        payload.error || `Request failed: ${response.status}`,
        response.status,
        payload.details
      );
    }

    const payload = await response.json();
    return payload.data as T;
  } finally {
    if (timeout) window.clearTimeout(timeout);
  }
};
